import React, { useRef, useState } from 'react';
import { Button, DatePicker, Timeline } from 'antd';
import {
	ArrowDownOutlined,
	ArrowUpOutlined,
	CloseCircleOutlined,
	DollarCircleOutlined,
	EyeOutlined,
	PrinterOutlined,
} from '@ant-design/icons';
import styled from 'styled-components';
import { Address, Client, ClientOrder, ClientOrderPaymentState, Container } from 'types';
import PageSection from 'components/page/pageSection';
import SearchSelector from '../../components/searchSelectorNew';
import { ClientOrdersPrint } from '../../prints';
import { useReactToPrint } from 'react-to-print';
import moment from 'moment';
import { OrderTimelineItem } from './order-timeline-item';

const Wrapper = styled.div`
	> div.page-section > div {
		margin-bottom: 4px;
		padding: 4px 0;
	}
`;

interface Props {
	client: Client;
	orders: ClientOrder[];
	addresses: Address[];
	containers: Container[];
	payOrder: (id: number) => Promise<void>;
}

export const OrdersSection: React.FC<Props> = ({ client, orders, addresses, containers, payOrder }) => {
	const [address, setAddress] = useState<string>();
	const [onlyOverdue, setOnlyOverdue] = useState(false);
	const [after, setAfter] = useState<moment.Moment | null>(null);
	const [before, setBefore] = useState<moment.Moment | null>(null);
	const [sort, setSort] = useState<'newest' | 'oldest'>('newest');

	const printRef = useRef();
	const handlePrint = useReactToPrint({
		content: () => printRef.current!,
	});

	const filteredOrders = orders.filter((order) => {
		if (address && order.address !== address) {
			return false;
		}

		if (onlyOverdue && order.paymentState !== ClientOrderPaymentState.overdue) {
			return false;
		}

		if (after && moment(order.date).isBefore(after, 'day')) {
			return false;
		}

		if (before && moment(order.date).isAfter(before, 'day')) {
			return false;
		}

		return true;
	});

	if (sort === 'newest') {
		filteredOrders.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
	} else {
		filteredOrders.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
	}

	return (
		<Wrapper>
			<PageSection
				title="Zamówienia"
				width={878}
				select={
					<Button type="primary" size="large" onClick={() => handlePrint()} icon={<PrinterOutlined />}>
						Drukuj wykaz
					</Button>
				}
			>
				<div className="flex flex-col gap-2">
					<div className="flex items-center gap-2">
						<p>Adres:</p>
						<SearchSelector
							id="name"
							width="100%"
							allowClear
							search={(search, e) => {
								if (!e.name) {
									return false;
								}

								const s = search.toLocaleLowerCase();
								return e.name.toLocaleLowerCase().includes(s);
							}}
							elements={[...addresses, { name: undefined, id: 'all' }]}
							buildElement={(address) => address.name ?? 'Wszystkie'}
							value={address}
							onChange={(a) => setAddress(a === 'all' ? undefined : a)}
						/>
					</div>
					<div className="flex items-center gap-2">
						<p>Data:</p>
						<DatePicker picker="date" placeholder="Data od" size="large" value={after} onChange={setAfter} />
						<DatePicker picker="date" placeholder="Data do" size="large" value={before} onChange={setBefore} />
						<Button
							type="primary"
							size="large"
							onClick={() => {
								setAfter(null);
								setBefore(null);
							}}
						>
							<CloseCircleOutlined />
						</Button>
					</div>
					<div className="flex gap-2 items-stretch">
						<Button
							className="flex-1"
							type="primary"
							size="large"
							onClick={() => setSort(sort === 'newest' ? 'oldest' : 'newest')}
							icon={sort === 'newest' ? <ArrowDownOutlined /> : <ArrowUpOutlined />}
						>
							Sortowane od {sort === 'newest' ? 'najnowszych' : 'najstarszych'}
						</Button>
						<Button
							className="flex-1"
							type="primary"
							size="large"
							onClick={() => setOnlyOverdue((u) => !u)}
							icon={!onlyOverdue ? <DollarCircleOutlined /> : <EyeOutlined />}
						>
							{!onlyOverdue ? 'Pokaż tylko zaległe' : 'Pokaż wszystkie'}
						</Button>
					</div>
				</div>
				{/* Print only done orders here as per client requirement. */}
				<ClientOrdersPrint ref={printRef} client={client} orders={filteredOrders.filter((o) => !!o.doneDate)} />
				<Timeline>
					{filteredOrders.map((order) => (
						<OrderTimelineItem
							key={`${order.type}#${order.id}`}
							order={order}
							container={order.containerId ? containers.find((c) => c.id === order.containerId) : undefined}
							payOrder={payOrder}
						/>
					))}
				</Timeline>
			</PageSection>
		</Wrapper>
	);
};
